import React, { useEffect, useState, useMemo } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import employeeRepository from "infra/repositories/employee-repository";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { useImpersonationStore } from "presentation/stores/use-impersonation-store";
import { Employee } from "domain/entities/employee";
import { httpClient } from "infra/axios/axios-api";
import { useIsMobile } from "presentation/mobile/hooks/useIsMobile";

interface ImpersonateDialogProps {
  visible: boolean;
  onHide: () => void;
}

const ImpersonateDialog: React.FC<ImpersonateDialogProps> = ({
  visible,
  onHide,
}) => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(
    null,
  );
  const [loading, setLoading] = useState(false);

  const isMobile = useIsMobile();

  const { enableImpersonation } = useImpersonationStore();
  const { userEmployee, userPermissions, setUserEmployee, setUserPermissions } =
    useAuthStore();

  useEffect(() => {
    if (visible) {
      loadEmployees();
    }
  }, [visible]);

  const loadEmployees = async () => {
    try {
      const response: any = await employeeRepository.getAll({
        page: 1,
        limit: 1000,
        isActive: true,
      } as any);

      if (response) {
        if (Array.isArray(response.data)) {
          setEmployees(response.data);
        } else if (response.data && Array.isArray(response.data.data)) {
          setEmployees(response.data.data);
        } else if (Array.isArray(response)) {
          setEmployees(response);
        }
      }
    } catch (error) {
      console.error("Erro ao carregar colaboradores:", error);
    }
  };

  const employeeOptions = useMemo(() => {
    return employees.map(
      (emp) =>
        ({
          id: emp.id,
          name: emp.name,
          type: "employee",
          value: emp.id,
        }) as any,
    );
  }, [employees]);

  const handleStartImpersonation = async () => {
    if (!selectedEmployeeId || !userEmployee || !userPermissions) return;

    setLoading(true);
    try {
      const selectedEmpFromList = employees.find((e) => e.id === selectedEmployeeId);
      if (!selectedEmpFromList) return;

      let selectedEmp = selectedEmpFromList;
      try {
        const empRes = await httpClient.get(`/employee/${selectedEmployeeId}`);
        if (empRes.data) {
          // A API devolve frequentemente { metadata: ..., data: {...} }
          selectedEmp = empRes.data.data ? empRes.data.data : empRes.data;
        }
      } catch (e) {
        console.warn("Erro ao buscar detalhes do colaborador.", e);
      }

      // Buscar as permissões do utilizador selecionado.
      let impersonatedPermissions: any[] = [];
      try {
        const userRes = await httpClient.get(`/user`, {
          params: { email: selectedEmp.emailAddress },
        });
        const impersonatedUserRoles = userRes.data?.data?.[0]?.roles || [];

        const permRes = await httpClient.get(`/permission`, {
          params: { limit: 100 },
        });
        const allPermissions = permRes.data?.data || [];

        impersonatedUserRoles.forEach((role: any) => {
          const selectedPermission = allPermissions.filter(
            (p: any) => p.roleId === role.id,
          );
          impersonatedPermissions = [
            ...impersonatedPermissions,
            ...selectedPermission,
          ];
        });
      } catch (e) {
        console.warn("Erro ao buscar permissões do utilizador.", e);
      }

      // Guardar na store de impersonation
      enableImpersonation(selectedEmployeeId, userEmployee, userPermissions);

      // Substituir na store principal para re-renderizar a UI
      setUserEmployee(selectedEmp);
      setUserPermissions(impersonatedPermissions);

      onHide();
      // Reload the page to ensure all menus and hooks re-evaluate
      window.location.reload();
    } catch (error) {
      console.error("Erro ao iniciar impersonation:", error);
    } finally {
      setLoading(false);
    }
  };

  const footer = (
    <div>
      <Button
        label="Cancelar"
        icon="pi pi-times"
        onClick={onHide}
        className="p-button-text"
      />
      <Button
        label="Iniciar Simulação"
        icon="pi pi-check"
        onClick={handleStartImpersonation}
        disabled={!selectedEmployeeId || loading}
        loading={loading}
      />
    </div>
  );

  return (
    <Dialog
      header="Ver como..."
      visible={visible}
      style={{ width: !isMobile ? "30vw" : "" }}
      footer={footer}
      onHide={onHide}
    >
      <div className="p-fluid">
        <div className="field">
          <label htmlFor="employee">Selecione o Colaborador</label>
          <Dropdown
            id="employee"
            value={selectedEmployeeId}
            options={employeeOptions}
            optionLabel="name"
            optionValue="id"
            onChange={(e) => setSelectedEmployeeId(e.value)}
            placeholder="Selecione..."
            filter
            className="w-full"
          />
        </div>
        <p className="mt-3 text-sm" style={{ color: "#666" }}>
          O modo de simulação irá afetar a sua visualização (menus e dados), mas
          as operações de auditoria continuarão a registar o seu utilizador
          real.
        </p>
      </div>
    </Dialog>
  );
};

export default ImpersonateDialog;
